import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaShieldAlt } from '@react-icons/all-files/fa/FaShieldAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPencilAlt } from '@react-icons/all-files/fa/FaPencilAlt';
import { FaSave } from '@react-icons/all-files/fa/FaSave';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import { FaSyncAlt } from '@react-icons/all-files/fa/FaSyncAlt';
import axios from 'axios';
import Breadcrumb from '../../../commons/Breadcrumb';
import PageHeader from '../../../commons/PageHeader';
import PageForm, { FormField } from '../../../commons/PageForm';
import PageAlert, { usePageAlert } from '../../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../manager/sys-perfil.reducer';
import PerfilSistemaTasksPanel, { PerfilSistemaTasksPanelHandle } from './perfil-sistema-tasks-panel';

const BACK = '/configuracion/seguridad/perfiles-sistema';
const ACCENT = '#6366f1';

const panel: React.CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  padding: '22px 24px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
  border: '1px solid #e8ecf1',
};

const editLayout: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
};

const editColumns: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 20,
  flexWrap: 'wrap',
};

const editPanelLeft: React.CSSProperties = {
  ...panel,
  flex: '0 1 300px',
  minWidth: 240,
  maxWidth: '100%',
};

const editPanelRight: React.CSSProperties = {
  ...panel,
  flex: '1 1 400px',
  minWidth: 280,
};

const editActions: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  paddingTop: 8,
  borderTop: '1px solid #f1f5f9',
};

const btnPrimary: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '9px 20px',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '9px 20px',
  background: '#f1f5f9',
  color: '#475569',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

interface SysPerfilForm {
  id: string;
  nombre: string;
}

const empty: SysPerfilForm = { id: '', nombre: '' };

const fields: FormField<SysPerfilForm>[] = [
  {
    name: 'id',
    label: 'ID',
    readOnly: true,
    editOnly: true,
  },
  {
    name: 'nombre',
    label: 'Nombre',
    required: true,
    placeholder: 'Nombre del perfil de sistema',
  },
];

export default function PerfilesSistemaFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { alert, showToast } = usePageAlert();
  const tasksRef = useRef<PerfilSistemaTasksPanelHandle>(null);

  const entity = useAppSelector(s => s.sysPerfil.entity) as any;
  const updating = useAppSelector(s => s.sysPerfil.updating);

  const [form, setForm] = useState<SysPerfilForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof SysPerfilForm, string>>>({});
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameModalError, setNameModalError] = useState<string | null>(null);
  const [savingPage, setSavingPage] = useState(false);
  const [tasksRefreshing, setTasksRefreshing] = useState(false);

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id: String(entity.id),
        nombre: entity.nombre ?? '',
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshTasksFromBackend = async () => {
    if (!tasksRef.current) return;
    setTasksRefreshing(true);
    try {
      await tasksRef.current.reload();
    } catch {
      showToast('error', 'No se pudieron actualizar los permisos desde el servidor.');
    } finally {
      setTasksRefreshing(false);
    }
  };

  const formBusy = updating || savingPage || tasksRefreshing;

  const handleFieldChange = (name: keyof SysPerfilForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof SysPerfilForm, string>> = {};
    if (!form.nombre.trim()) errs.nombre = 'El nombre es requerido';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const payload = {
      ...(isNew ? {} : { id: Number(form.id) }),
      nombre: form.nombre.trim(),
    };

    setSavingPage(true);
    try {
      if (isNew) {
        await dispatch(createEntity(payload)).unwrap();
        dispatch(reset());
        navigate(BACK, {
          state: {
            successMsg: 'Perfil de sistema creado correctamente.',
          },
        });
      } else {
        await dispatch(updateEntity(payload)).unwrap();
        await tasksRef.current?.saveAssignments();
        showToast('success', 'Perfil y permisos guardados correctamente.', {
          actionLabel: 'Aceptar',
          onAction: () => {
            dispatch(reset());
            navigate(BACK);
          },
        });
      }
    } catch (err: unknown) {
      let msg = isNew ? 'No se pudo crear el perfil.' : 'No se pudo guardar el perfil o los permisos.';
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { message?: string; detail?: string; title?: string } | undefined;
        msg = data?.detail ?? data?.message ?? data?.title ?? err.message ?? msg;
      } else if (err instanceof Error && err.message) {
        msg = err.message;
      }
      showToast('error', msg);
    } finally {
      setSavingPage(false);
    }
  };

  const openNameModal = () => {
    setNameDraft(form.nombre);
    setNameModalError(null);
    setNameModalOpen(true);
  };

  const modalNameSaving = updating && nameModalOpen;

  const nombreSaveErrorMessage = (err: unknown): string => {
    let msg = 'No se pudo guardar el nombre.';
    if (axios.isAxiosError(err)) {
      const data = err.response?.data as { message?: string; detail?: string; title?: string } | undefined;
      msg = data?.detail ?? data?.message ?? data?.title ?? err.message ?? msg;
    } else if (err instanceof Error && err.message) {
      msg = err.message;
    }
    return msg;
  };

  const applyNameFromModal = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setNameModalError('El nombre es requerido');
      return;
    }
    if (!form.id) return;

    setNameModalError(null);
    try {
      await dispatch(updateEntity({ id: Number(form.id), nombre: trimmed })).unwrap();
      setForm(prev => ({ ...prev, nombre: trimmed }));
      setErrors(prev => ({ ...prev, nombre: undefined }));
      setNameModalOpen(false);
      showToast('success', 'Nombre del perfil guardado correctamente.');
    } catch (err: unknown) {
      setNameModalOpen(false);
      showToast('error', nombreSaveErrorMessage(err));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb
        items={[
          { label: 'Inicio', to: '/', icon: <FaHome /> },
          { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
          { label: 'Seguridad', to: '/configuracion/seguridad', icon: <FaShieldAlt /> },
          { label: 'Perfiles de Sistema', to: BACK, icon: <FaShieldAlt /> },
          { label: isNew ? 'Nuevo perfil' : `Editar #${id}` },
        ]}
      />

      <PageHeader
        icon={<FaShieldAlt />}
        iconColor="#6366f1"
        title={isNew ? 'Nuevo Perfil de Sistema' : 'Editar Perfil de Sistema'}
        subtitle={
          isNew ? 'Completa los campos para crear un nuevo perfil' : `Modificando perfil #${id}`
        }
      />

      {alert && <PageAlert alert={alert} />}

      {isNew ? (
        <PageForm
          fields={fields}
          values={form}
          errors={errors}
          isNew
          saving={updating}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
          onCancel={() => navigate(BACK)}
          accentColor={ACCENT}
        />
      ) : (
        <>
          <form onSubmit={handleSubmit} style={editLayout}>
            <div style={editColumns}>
              <div style={editPanelLeft}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#475569', userSelect: 'none' }}>Nombre</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', wordBreak: 'break-word' }}>
                      {form.nombre.trim() ? form.nombre : '—'}
                    </span>
                    <button
                      type="button"
                      title="Editar nombre"
                      onClick={openNameModal}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        background: '#f8fafc',
                        color: '#475569',
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    >
                      <FaPencilAlt size={14} />
                    </button>
                  </div>
                  {errors.nombre && (
                    <span style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>{errors.nombre}</span>
                  )}
                </div>
              </div>

              <div style={editPanelRight}>
                {id && (
                  <PerfilSistemaTasksPanel
                    ref={tasksRef}
                    perfilId={id}
                    accentColor={ACCENT}
                    disabled={formBusy}
                  />
                )}
              </div>
            </div>

            <div style={editActions}>
              <button type="button" style={btnSecondary} onClick={() => navigate(BACK)} disabled={formBusy}>
                <FaArrowLeft style={{ marginRight: 6 }} />
                Cancelar
              </button>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" style={btnSecondary} onClick={() => void refreshTasksFromBackend()} disabled={formBusy}>
                  <FaSyncAlt style={{ marginRight: 6 }} />
                  {tasksRefreshing ? 'Refrescando…' : 'Refrescar'}
                </button>
                <button type="submit" style={{ ...btnPrimary, background: ACCENT }} disabled={formBusy}>
                  <FaSave style={{ marginRight: 6 }} />
                  {updating || savingPage ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </form>

          {nameModalOpen && (
            <div
              role="presentation"
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15, 23, 42, 0.45)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
              }}
              onMouseDown={e => {
                if (e.target === e.currentTarget && !modalNameSaving) {
                  setNameModalOpen(false);
                  setNameModalError(null);
                }
              }}
            >
              <div
                role="dialog"
                aria-modal
                aria-labelledby="perfil-nombre-modal-title"
                style={{
                  background: '#fff',
                  borderRadius: 14,
                  padding: '24px 28px',
                  maxWidth: 420,
                  width: '100%',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                }}
                onMouseDown={e => e.stopPropagation()}
              >
                <h2
                  id="perfil-nombre-modal-title"
                  style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700, color: '#0f172a' }}
                >
                  Editar nombre
                </h2>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                  Nombre del perfil
                </label>
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={e => {
                    setNameDraft(e.target.value);
                    setNameModalError(null);
                  }}
                  placeholder="Nombre del perfil de sistema"
                  className={`profase-form-input${nameModalError ? ' profase-form-input--error' : ''}`}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
                {nameModalError && (
                  <span style={{ display: 'block', fontSize: 11, color: '#ef4444', marginTop: 6 }}>{nameModalError}</span>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                  <button
                    type="button"
                    style={btnSecondary}
                    disabled={modalNameSaving}
                    onClick={() => {
                      setNameModalOpen(false);
                      setNameModalError(null);
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    style={{
                      ...btnPrimary,
                      background: ACCENT,
                      ...(modalNameSaving ? { opacity: 0.85, cursor: 'not-allowed' } : {}),
                    }}
                    disabled={modalNameSaving}
                    onClick={() => void applyNameFromModal()}
                  >
                    {modalNameSaving ? (
                      <>
                        <FaSpinner style={{ marginRight: 6, animation: 'spin 1s linear infinite' }} />
                        Guardando…
                      </>
                    ) : (
                      'Aceptar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
